import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, Divider, TextField, IconButton,
  Avatar, Snackbar, Alert, Container, Skeleton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Share as ShareIcon, Delete as DeleteIcon,
  Send as SendIcon, Visibility, Edit as EditIcon, Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { blogService, type BlogArticle } from '../services/blogService';
import { isSiteOwnerEmail } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { PollDisplay } from '../components/BlogPoll';

const REACTIONS = [
  { key: 'admiration', emoji: '🤩', label: 'Восхищение' },
  { key: 'surprise', emoji: '😲', label: 'Удивление' },
  { key: 'laugh', emoji: '😂', label: 'Смех' },
  { key: 'dislike', emoji: '👎', label: 'Неприятие' },
];

const BlogArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [commentError, setCommentError] = useState('');
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [replyName, setReplyName] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [polls, setPolls] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    blogService.getArticle(Number(id)).then(a => {
      setArticle(a);
      if (isAuthenticated) {
        blogService.listSubscriptions().then(subs => {
          setSubscribed(subs.some(s => s.author_id === a.author_id));
        }).catch(() => {});
      }
      blogService.getPolls(a.id).then(setPolls).catch(() => {});
      // Scroll to comment if hash is present
      setTimeout(() => {
        const hash = window.location.hash;
        if (hash && hash.startsWith('#comment-')) {
          const el = document.getElementById(hash.slice(1));
          if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.style.outline = '2px solid #1565c0'; el.style.borderRadius = '8px'; setTimeout(() => { el.style.outline = ''; }, 3000); }
        }
      }, 300);
    }).catch(() => navigate('/blog')).finally(() => setLoading(false));
  }, [id, isAuthenticated]);

  const handleReaction = async (reaction: string) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const result = await blogService.setReaction(Number(id), reaction);
      if (article) setArticle({ ...article, reactions: result.reactions });
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.detail || 'Ошибка', severity: 'error' }); }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    try {
      const c = await blogService.addComment(Number(id), commentText, replyTo || undefined);
      if (article) setArticle({ ...article, comments: [...(article.comments || []), c], comments_count: (article.comments_count || 0) + 1 });
      setCommentText('');
      setCommentError('');
      setReplyTo(null);
      setReplyName('');
    } catch (e: any) {
      setCommentError(e.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await blogService.deleteComment(commentId);
      if (article) setArticle({ ...article, comments: (article.comments || []).filter(c => c.id !== commentId), comments_count: Math.max(0, (article.comments_count || 1) - 1) });
    } catch {}
  };

  const handleCommentReaction = async (commentId: number, reaction: string) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const result = await blogService.setCommentReaction(commentId, reaction);
      if (article) {
        const updatedComments = (article.comments || []).map(c =>
          c.id === commentId ? { ...c, reactions: result.reactions } : c
        );
        setArticle({ ...article, comments: updatedComments });
      }
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.detail || 'Ошибка', severity: 'error' }); }
  };

  const handleSubscribeAuthor = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!article) return;
    if (subscribed) {
      // Find and delete subscription
      try {
        const subs = await blogService.listSubscriptions();
        const sub = subs.find(s => s.author_id === article.author_id);
        if (sub) { await blogService.deleteSubscription(sub.id); setSubscribed(false); setSnackbar({ open: true, message: 'Подписка отменена', severity: 'info' }); }
      } catch {}
    } else {
      try {
        await blogService.createSubscription({ author_id: article.author_id, delivery_method: 'notification' });
        setSubscribed(true);
        setSnackbar({ open: true, message: `Вы подписались на статьи ${article.author_name}`, severity: 'success' });
      } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.detail || 'Ошибка', severity: 'error' }); }
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: article?.title, url }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); setSnackbar({ open: true, message: 'Ссылка скопирована!', severity: 'success' }); } catch {}
    }
  };

  const handleDeleteArticle = async () => {
    if (!window.confirm('Удалить статью? Действие нельзя отменить.')) return;
    try {
      await blogService.deleteArticle(Number(id));
      setSnackbar({ open: true, message: 'Статья удалена', severity: 'success' });
      setTimeout(() => navigate('/blog'), 400);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.detail || 'Ошибка удаления статьи', severity: 'error' });
    }
  };

  const startReply = (commentId: number, userName: string) => {
    setReplyTo(commentId);
    setReplyName(userName);
    setCommentText('');
  };

  // Auto-link URLs in HTML content
  const linkifyHtml = (html: string) => {
    // First add target=_blank to existing <a> tags
    let result = html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
    // Then auto-detect bare URLs not inside <a> or <img> tags
    result = result.replace(/(?<!["=])(https?:\/\/[^\s<"]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:#1565c0;text-decoration:underline">$1</a>');
    return result;
  };

  if (loading) return <Container maxWidth="md"><Box sx={{ py: 3 }}><Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} /></Box></Container>;
  if (!article) return null;

  const isAuthor = Number(user?.id) === Number(article.author_id);
  const isSiteAdmin = user?.role === 'site_admin';
  const isSiteOwner = isSiteOwnerEmail(user?.email);
  // Кнопка «Редактировать» — только автору статьи
  const canEditArticle = isAuthor;
  const canDeleteArticle = article.can_delete || isAuthor || isSiteAdmin || isSiteOwner;

  return (
    <Container maxWidth="md">
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/blog')} size="small" sx={{ mb: 2 }}>К блогу</Button>

        {/* Article */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
            {article.event_type_name && <Chip size="small" label={`${article.event_type_emoji || ''} ${article.event_type_name}`} />}
            {article.city_name && <Chip size="small" label={article.city_name} variant="outlined" />}
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, mb: 1 }}>{article.title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600} color="primary" component={RouterLink} to={`/blog?author_id=${article.author_id}`} sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              {article.author_name}
            </Typography>
            {canEditArticle && <IconButton size="small" onClick={() => navigate(`/blog/${id}/edit`)}><EditIcon fontSize="small" /></IconButton>}
            {!isAuthor && isAuthenticated && (
              <Button size="small" variant={subscribed ? 'contained' : 'outlined'}
                startIcon={<NotificationsIcon sx={{ fontSize: 14 }} />}
                onClick={handleSubscribeAuthor}
                sx={{ fontSize: '0.72rem', py: 0, px: 1, minWidth: 0, ...(subscribed ? { bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } } : {}) }}>
                {subscribed ? 'Подписан' : 'Подписаться'}
              </Button>
            )}
            <Typography variant="caption" color="text.secondary">· {article.published_at ? new Date(article.published_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
              <Visibility sx={{ fontSize: 14, color: '#999' }} />
              <Typography variant="caption" color="text.secondary">{article.views_count}</Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ lineHeight: 1.7, fontSize: { xs: '0.9rem', sm: '1rem' }, '& img': { maxWidth: '100%', borderRadius: 1, my: 1 }, '& h2': { mt: 2, mb: 1 }, '& h3': { mt: 1.5, mb: 0.5 }, '& blockquote': { borderLeft: '3px solid #e2e8f0', pl: 2, ml: 0, color: '#64748b' }, '& a': { color: '#1565c0', textDecoration: 'underline', cursor: 'pointer' }, '& ul, & ol': { pl: 3 } }}>
            {(() => {
              const content = article.content || '';
              const isHtml = content.trim().startsWith('<');

              // Extract poll markers from content (both HTML and plain text)
              const pollRegex = /\[poll:(\d+)\]/g;
              const parts: Array<{ type: 'html'; content: string } | { type: 'poll'; pollId: number }> = [];
              let lastIndex = 0;
              let match;
              while ((match = pollRegex.exec(content)) !== null) {
                if (match.index > lastIndex) {
                  parts.push({ type: 'html', content: content.slice(lastIndex, match.index) });
                }
                parts.push({ type: 'poll', pollId: parseInt(match[1]) });
                lastIndex = match.index + match[0].length;
              }
              if (lastIndex < content.length) {
                parts.push({ type: 'html', content: content.slice(lastIndex) });
              }

              // If no polls found, render content as-is
              if (parts.length === 0 || (parts.length === 1 && parts[0].type === 'html')) {
                if (isHtml) {
                  return <div dangerouslySetInnerHTML={{ __html: linkifyHtml(content) }} />;
                }
                return content.split('\n').map((line, i) => {
                  if (line.startsWith('## ')) return <Typography key={i} variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>{line.slice(3)}</Typography>;
                  if (line.startsWith('### ')) return <Typography key={i} variant="subtitle1" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>{line.slice(4)}</Typography>;
                  if (line.startsWith('- ')) return <Typography key={i} variant="body2" sx={{ ml: 2, mb: 0.5 }}>• {line.slice(2)}</Typography>;
                  if (line.trim() === '') return <Box key={i} sx={{ height: 8 }} />;
                  return <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>{line}</Typography>;
                });
              }

              // Render parts with inline polls
              return parts.map((part, i) => {
                if (part.type === 'poll') {
                  const poll = polls.find((p: any) => p.id === part.pollId);
                  if (poll) {
                    return <Box key={`poll-${i}`} sx={{ my: 2 }}><PollDisplay poll={poll} onVoted={() => blogService.getPolls(article.id).then(setPolls)} /></Box>;
                  }
                  return null;
                }
                // HTML part — strip trailing [poll:X] that might be partially in HTML
                let html = part.content.replace(/<p>\s*\[poll:\d+\]\s*<\/p>/gi, '');
                html = html.replace(/<p>\s*$/i, '').replace(/^\s*<\/p>/i, '');
                html = linkifyHtml(html);
                if (!html.trim()) return null;
                return <div key={`html-${i}`} dangerouslySetInnerHTML={{ __html: html }} />;
              });
            })()}
          </Box>
        </Paper>

        {/* Polls without position marker (standalone at bottom for backwards compat) */}
        {polls.filter((p: any) => !article.content.includes(`[poll:${p.id}]`)).length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {polls.filter((p: any) => !article.content.includes(`[poll:${p.id}]`)).map((poll: any) => (
              <PollDisplay key={poll.id} poll={poll} onVoted={() => blogService.getPolls(article.id).then(setPolls)} />
            ))}
          </Box>
        )}

        {/* Reactions + Share + Subscribe */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
              {REACTIONS.map(r => (
                <Button key={r.key} size="small" variant="outlined"
                  onClick={() => handleReaction(r.key)}
                  sx={{ minWidth: 'auto', px: { xs: 1, sm: 1.5 }, fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
                  {r.emoji} {article.reactions?.[r.key] || 0}
                </Button>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {!isAuthor && isAuthenticated && (
                <Button size="small" variant={subscribed ? 'contained' : 'outlined'}
                  startIcon={<NotificationsIcon sx={{ fontSize: 14 }} />}
                  onClick={handleSubscribeAuthor}
                  sx={{ fontSize: '0.72rem', ...(subscribed ? { bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } } : {}) }}>
                  {subscribed ? 'Подписан' : 'Подписаться'}
                </Button>
              )}
              <Button size="small" startIcon={<ShareIcon />} onClick={handleShare} sx={{ fontSize: '0.78rem' }}>
                Поделиться
              </Button>
            </Box>
          </Box>
          {canDeleteArticle && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
              <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteArticle}
                sx={{ fontSize: '0.78rem' }}>
                Удалить статью
              </Button>
            </Box>
          )}
        </Paper>

        {/* Comments */}
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Комментарии ({article.comments?.length || 0})</Typography>
          <Divider sx={{ mb: 1.5 }} />

          {(article.comments || []).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Пока нет комментариев</Typography>
          )}

          {/* Group comments: parents first, replies nested */}
          {(() => {
            const comments = article.comments || [];
            const parents = comments.filter(c => !c.parent_comment_id);
            const childrenMap: Record<number, typeof comments> = {};
            comments.forEach(c => {
              if (c.parent_comment_id) {
                if (!childrenMap[c.parent_comment_id]) childrenMap[c.parent_comment_id] = [];
                childrenMap[c.parent_comment_id].push(c);
              }
            });

            const renderComment = (c: typeof comments[0], isReply = false, parentAuthor?: string) => {
              const isOwnComment = user?.id === c.user_id;
              return (
              <Box key={c.id} id={`comment-${c.id}`} sx={{
                display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start',
                ml: isReply ? { xs: 2, sm: 4 } : 0,
                borderLeft: isReply ? '3px solid #1565c0' : 'none',
                pl: isReply ? 1.5 : 0,
                bgcolor: isReply ? '#f0f4ff' : 'transparent',
                borderRadius: isReply ? 1 : 0,
                py: isReply ? 1 : 0,
                pr: isReply ? 1 : 0,
              }}>
                <Avatar sx={{ width: isReply ? 24 : 28, height: isReply ? 24 : 28, fontSize: isReply ? '0.6rem' : '0.7rem', bgcolor: isReply ? '#42a5f5' : '#1565c0' }}>{(c.user_name || '?')[0]}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" fontWeight={700}>{c.user_name}</Typography>
                    {isReply && parentAuthor && (
                      <Typography variant="caption" color="#1565c0" sx={{ fontSize: '0.65rem' }}>→ {parentAuthor}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">{c.created_at ? new Date(c.created_at).toLocaleDateString('ru-RU') : ''}</Typography>
                    {user && (user.id === c.user_id || user.role === 'site_admin') && (
                      <IconButton size="small" onClick={() => handleDeleteComment(c.id)} sx={{ p: 0, ml: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: 14, color: '#999' }} />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{c.content}</Typography>
                  {/* Reactions row */}
                  <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 }, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {REACTIONS.map(r => {
                      const count = c.reactions?.[r.key] || 0;
                      return (
                        <Button key={r.key} size="small" variant="text"
                          onClick={() => !isOwnComment && handleCommentReaction(c.id, r.key)}
                          disabled={isOwnComment}
                          sx={{
                            minWidth: 'auto', px: { xs: 0.5, sm: 0.75 }, py: 0,
                            fontSize: { xs: '0.65rem', sm: '0.72rem' },
                            color: isOwnComment ? '#ccc' : count > 0 ? '#1e293b' : '#999',
                            lineHeight: 1.4, cursor: isOwnComment ? 'default' : 'pointer',
                          }}>
                          {r.emoji}{count > 0 && count}
                        </Button>
                      );
                    })}
                    {isAuthenticated && !isReply && (
                      <IconButton size="small" onClick={() => startReply(c.id, c.user_name || 'User')} sx={{ p: 0, ml: 0.25 }} title="Ответить">
                        <SendIcon sx={{ fontSize: 12, color: '#1565c0' }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
              );
            };

            return parents.map(parent => (
              <React.Fragment key={parent.id}>
                {renderComment(parent)}
                {(childrenMap[parent.id] || []).map(child => renderComment(child, true, parent.user_name || undefined))}
              </React.Fragment>
            ));
          })()}

          {isAuthenticated && (
            <Box sx={{ mt: 2 }}>
              {replyTo && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" color="primary">Ответ @{replyName}</Typography>
                  <IconButton size="small" onClick={() => { setReplyTo(null); setReplyName(''); }} sx={{ p: 0 }}>
                    <DeleteIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              )}
              {commentError && <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem' }} onClose={() => setCommentError('')}>{commentError}</Alert>}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField fullWidth size="small" placeholder={replyTo ? `Ответ @${replyName}...` : 'Комментарий (до 300 символов)...'}
                  value={commentText} onChange={e => { setCommentText(e.target.value); setCommentError(''); }}
                  inputProps={{ maxLength: 300 }}
                  helperText={`${commentText.length}/300`}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); } }} />
                <IconButton color="primary" onClick={handleComment} disabled={!commentText.trim()}>
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default BlogArticlePage;